// crewbit-admin/src/hooks/useReassignScheduleEntry.ts
//
// Schedule Module Phase B.1 — paired hook for `reassign_schedule_entry` RPC
// (Migration 312 + A13 corrections in 313).
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §4.4.
//
// Path A consumer (admin only — operators rejected by RPC body with
// OPERATOR_CANNOT_REASSIGN). Backs drag-drop reassign on Schedule + Dispatch
// surfaces. RPC body queues pending_notifications for old + new assignees
// (Foundation v1 §D2 / gap 0.9 closure).

import { useCallback } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';
import type { ConflictDetail } from '../lib/errorMap';

export interface ReassignScheduleEntryInput {
  entryId: string;
  /** public.users.id of the new assignee. Server queues notifications for old + new. */
  newAssigneeId: string;
  idempotencyKey?: string;
  force?: boolean;
}

export type ReassignScheduleEntryResult = ScheduleRpcResult<{
  success: true;
  entry_id?: string;
  conflicts?: ConflictDetail[];
  already_assigned?: boolean;
}>;

export function useReassignScheduleEntry() {
  return useCallback(
    async (input: ReassignScheduleEntryInput): Promise<ReassignScheduleEntryResult> => {
      return callScheduleRpc<{
        success: true;
        entry_id?: string;
        conflicts?: ConflictDetail[];
        already_assigned?: boolean;
      }>('reassign_schedule_entry', {
        p_entry_id: input.entryId,
        p_new_assignee_id: input.newAssigneeId,
        p_idempotency_key: input.idempotencyKey ?? null,
        p_force: input.force ?? false,
      });
    },
    [],
  );
}
